import React, { useState, useEffect } from 'react';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import CircularProgress from "@mui/material/CircularProgress";
import { Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { apiRequest } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

function statusColor(s) {
  if (s === "F" || s === "fulfilled") return "#01B574";
  if (s === "O" || s === "open")      return "#F7B731";
  return "rgba(255,255,255,0.5)";
}

function statusLabel(s) {
  if (s === "F" || s === "fulfilled") return "Fulfilled";
  if (s === "O" || s === "open")      return "Open";
  return s || "—";
}

function reasonMessage(reason) {
  switch (reason) {
    case "missing_vrn":
      return "No VAT obligations could be loaded because this client does not have a VAT registration number (VRN) saved yet.";
    case "missing_ids":
      return "The VAT sync could not run because the tenant/client identifiers were missing.";
    case "missing_credentials":
      return "HMRC credentials are not available for this client yet.";
    default:
      return "No VAT obligations were returned for this client yet.";
  }
}

/*Bearer ${token}`
 * VATObligationsList
 *
 * Props:
 *   tenantId {string}
 *   clientId {string}
 *   onSelectPeriod {function} — called with periodKey when "File Return" clicked
 */
function VATObligationsList({ tenantId, clientId, onSelectPeriod }) {
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [syncMessage, setSyncMessage] = useState("");

  const loadObligations = async () => {
    if (!tenantId || !clientId) return;
    setLoading(true); setError(null);
    try {
      const data = await apiRequest(
        `/hmrc/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/obligations`
      );
      setObligations(data.obligations || []);
      if (data.skipped) {
        setSyncMessage(data.message || reasonMessage(data.reason));
      } else {
        setSyncMessage("");
      }
    } catch (e) {
      setError(e.message || "Failed to load obligations");
      setSyncMessage("");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadObligations(); }, [tenantId, clientId]); // eslint-disable-line

  if (loading) {
    return <VuiBox display="flex" alignItems="center" gap={1} py={2}><CircularProgress size={16} sx={{ color: TC.secondary }} /><VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>Loading obligations from HMRC…</VuiTypography></VuiBox>;
  }

  if (error) {
    return (
      <VuiBox role="alert">
        <VuiTypography sx={{ color: "#f44336", fontSize: 13, mb: 1 }}>{error}</VuiTypography>
        <VuiButton size="small" color="info" variant="outlined" onClick={loadObligations} sx={{ fontSize: 12 }} aria-label="Retry loading VAT obligations">Retry</VuiButton>
      </VuiBox>
    );
  }

  if (obligations.length === 0) {
    return (
      <VuiBox py={2} textAlign="center">
        <VuiTypography sx={{ color: TC.secondary, fontSize: 13, mb: 1 }}>
          {syncMessage || "No obligations found. Connect to HMRC to fetch live data."}
        </VuiTypography>
        <VuiButton size="small" color="info" variant="outlined" onClick={loadObligations} sx={{ fontSize: 12 }} aria-label="Refresh VAT obligations">Refresh</VuiButton>
      </VuiBox>
    );
  }

  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="flex-end" mb={1}>
        <VuiButton size="small" color="info" variant="outlined" onClick={loadObligations} sx={{ fontSize: 11 }} aria-label="Refresh VAT obligations"><span aria-hidden="true">↻</span> Refresh</VuiButton>
      </VuiBox>
      <Table size="small" aria-label="VAT obligations">
        <caption style={{ captionSide: "top", textAlign: "left", fontSize: 0, height: 0, overflow: "hidden" }}>VAT return obligations from HMRC</caption>
        <TableHead>
          <TableRow>
            {["Period", "Start", "End", "Due", "Status", "Action"].map(h => (
              <TableCell key={h} scope="col" sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e4eef8", pb: 1 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {obligations.map((ob, idx) => {
            const key = ob.periodKey || `${ob.start}/${ob.end}`;
            const color = statusColor(ob.status);
            return (
              <TableRow key={idx}>
                <TableCell sx={{ color: TC.midtone, fontSize: 13, borderBottom: "1px solid #e4eef8" }}>{key}</TableCell>
                <TableCell sx={{ color: TC.secondary, fontSize: 12, borderBottom: "1px solid #e4eef8" }}>{ob.start}</TableCell>
                <TableCell sx={{ color: TC.secondary, fontSize: 12, borderBottom: "1px solid #e4eef8" }}>{ob.end}</TableCell>
                <TableCell sx={{ color: TC.secondary, fontSize: 12, borderBottom: "1px solid #e4eef8" }}>{ob.due}</TableCell>
                <TableCell sx={{ borderBottom: "1px solid #e4eef8" }}>
                  <Chip label={statusLabel(ob.status)} size="small" sx={{ background: `${color}18`, color, border: `1px solid ${color}44`, fontSize: 11, fontWeight: 700, height: 20 }} />
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid #e4eef8" }}>
                  {(ob.status === "O" || ob.status === "open") && onSelectPeriod && (
                    <VuiButton size="small" color="info" variant="contained"
                      onClick={() => onSelectPeriod(key)} sx={{ fontSize: 11, py: 0.3, px: 1.2 }}
                      aria-label={`File VAT return for period ${key}`}>
                      File Return
                    </VuiButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </VuiBox>
  );
}

export default VATObligationsList;




